import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { EMPTY, catchError, distinctUntilChanged, filter, map, merge, skip, switchMap } from 'rxjs';
import { Router } from '@angular/router';
import { MonitoringPreferencesService } from './monitoring-preferences.service';
import { RequestsService } from './utils/requests.service';
import { SharedService } from './shared.service';
import { AlertsService } from './alerts.service';
import { RingtoneService } from './utils/ringtone.service';
import { UsageTrackingService } from './usage-tracking.service';

@Injectable({ providedIn: 'root' })
export class MonitoringSessionService {
  private readonly preferences = inject(MonitoringPreferencesService);
  private readonly requests = inject(RequestsService);
  private readonly shared = inject(SharedService);
  private readonly alerts = inject(AlertsService);
  private readonly dialogs = inject(MatDialog);
  private readonly ringtone = inject(RingtoneService);
  private readonly router = inject(Router);
  private readonly usage = inject(UsageTrackingService);
  private initialized = false;

  initialize(): void {
    if (this.initialized) return;
    this.initialized = true;
    this.preferences.invalidated$.subscribe(() => {
      this.shared.clearAlerts();
      this.ringtone.stopRingtone();
      this.dialogs.closeAll();
      // A detail opened before the change may now be excluded. Reopen it from the refreshed list.
      if (/^\/wita\/devices\//.test(this.router.url)) void this.router.navigate(['/wita/devices'], { replaceUrl: true });
      if (/^\/wita\/structure\/edit_/.test(this.router.url)) void this.router.navigate(['/wita/structure'], { replaceUrl: true });
    });
    this.shared.managedAlert$.pipe(
      filter(alert => !!alert && (alert.buildingId || alert.facilities?.building?.id) === this.preferences.activeBuildingId),
      switchMap(() => this.preferences.refreshCounts().pipe(catchError(() => EMPTY)))
    ).subscribe();
    merge(
      this.requests.currentUser$.pipe(map(user => `${user?.id}:${user?.role}:${user?.customer?.id}:${user?.organization?.id}:${user?.building?.id}`), distinctUntilChanged()),
      this.preferences.refreshRequested$,
      this.shared.connected$,
      this.usage.foreground$.pipe(distinctUntilChanged(), skip(1), filter(Boolean))
    ).pipe(switchMap(() => {
      const buildingId = this.preferences.activeBuildingId;
      if (!buildingId || !this.requests.getCurrentUser()?.id) return EMPTY;
      return this.preferences.load(buildingId).pipe(
        switchMap(selection => merge(
          this.preferences.refreshCounts().pipe(catchError(() => EMPTY)),
          selection.state === 'READY' ? this.alerts.getActiveAlertsList$().pipe(
            map(alerts => { this.shared.alertsList = alerts; }), catchError(() => EMPTY)
          ) : EMPTY
        )),
        catchError(() => { this.preferences.failed(); return EMPTY; })
      );
    })).subscribe();
  }
}
